import { useEffect, useRef, useState } from "react";
import Icon from "../Icon";
import { usePosicionDesplegable } from "../posicionDesplegable";
import * as api from "../../api";
import { manana } from "./fechas";
import { textoPlan, diasDeLaSemana, HORAS_AVISO, HORA_DEFECTO, etiquetaDia } from "./planes";

// «Planificar»: un solo boton que dice donde esta la tarea y abre un menu con atajos
// (Hoy, Mañana, Esta semana), la tira Lun-Dom con cuantas tareas tiene ya cada dia y el
// recordatorio en Microsoft To Do. Mis tareas funciona entera sin Microsoft: si no esta
// conectado o falla, solo se desactiva o avisa el bloque del recordatorio.
//
// onPlan(tarea, cuerpo) y onRecordatorio(tarea, cuerpo) los pone la pagina; lanzan el
// error del servidor (con `code`). El de plan devuelve la respuesta.
export default function PlanificarMenu({ tarea: t, conteo = {}, tope = 5, hoy, onPlan, onRecordatorio, onConectarMicrosoft }) {
  const [abierto, setAbierto] = useState(false);
  const [aviso, setAviso] = useState(null);       // { texto, reintentar? }
  const [lleno, setLleno] = useState(null);        // cuerpo pendiente de confirmar (hoy ya tiene 5)
  const [hora, setHora] = useState(t.recordatorio?.hora || HORA_DEFECTO);
  const [ms, setMs] = useState(null);              // estado de Microsoft, al abrir
  const ref = useRef(null);
  const boton = useRef(null);
  const panelRef = useRef(null);
  const { estilo } = usePosicionDesplegable(abierto, ref, panelRef, { separacion: 6 });
  const plan = t.plan || null;
  const dias = diasDeLaSemana(hoy);
  const diasQueVienen = diasDeLaSemana(hoy, 1);
  const hoyIso = dias.find(d => d.esHoy)?.iso;

  useEffect(() => {
    if (!abierto) return undefined;
    panelRef.current?.querySelector("button:not(:disabled)")?.focus();
    const fuera = e => { if (ref.current && !ref.current.contains(e.target)) setAbierto(false); };
    document.addEventListener("mousedown", fuera);
    let vivo = true;
    api.getOutlookEstado().then(r => vivo && setMs(r || {})).catch(() => vivo && setMs({ desconocido: true }));
    return () => { vivo = false; document.removeEventListener("mousedown", fuera); };
  }, [abierto]);

  const cerrar = () => { setAbierto(false); setLleno(null); setAviso(null); boton.current?.focus(); };
  // Es un dialogo: Escape cierra y Tab no se sale de el mientras esta abierto (en movil tapa la pagina)
  const teclado = e => {
    if (e.key === "Escape") { e.preventDefault(); cerrar(); return; }
    if (e.key !== "Tab" || !abierto || !panelRef.current) return;
    const focos = [...panelRef.current.querySelectorAll("button:not(:disabled), select, input")];
    if (!focos.length) return;
    const primero = focos[0], ultimo = focos[focos.length - 1];
    if (!panelRef.current.contains(document.activeElement)) { e.preventDefault(); primero.focus(); }
    else if (e.shiftKey && document.activeElement === primero) { e.preventDefault(); ultimo.focus(); }
    else if (!e.shiftKey && document.activeElement === ultimo) { e.preventDefault(); primero.focus(); }
  };

  const planificar = async cuerpo => {
    setAviso(null);
    try { await onPlan(t, cuerpo); setLleno(null); setAbierto(false); }
    catch (e) {
      if (e?.code === "DIA_LLENO") setLleno(cuerpo);
      else setAviso({ texto: e?.message || "No se ha podido planificar" });
    }
  };
  const ponerRecordatorio = async () => {
    setAviso(null);
    try {
      const r = await onRecordatorio(t, { hora: hora === "" ? null : hora });
      const est = r?.recordatorio?.estado;
      if (est === "error") setAviso({ texto: "No se ha podido crear el recordatorio. La tarea sigue planificada; solo falta el aviso en To Do.", reintentar: true });
      else if (est === "sin_conexion") setMs({ conectado: false });
      else if (est === "sin_permiso") setMs({ conectado: true, todo: false });
      else setAbierto(false);
    } catch (e) {
      if (e?.code === "SIN_DIA") setAviso({ texto: "Elige primero un día: el recordatorio necesita fecha." });
      else setAviso({ texto: "No se ha podido crear el recordatorio. La tarea sigue planificada; solo falta el aviso en To Do.", reintentar: true });
    }
  };

  const texto = textoPlan(plan, hoy);
  const puesta = !!(plan && plan.tipo);
  const tieneDia = !!(plan && plan.dia);
  const sinMs = ms && ms.conectado === false;
  const sinPermiso = ms && ms.conectado === true && ms.todo === false;
  const lleno5 = (conteo[hoyIso] || 0) >= tope && plan?.dia !== hoyIso;

  return (
    <div ref={ref} className="mt-plan" onKeyDown={teclado}>
      <button ref={boton} type="button" className={"mt-plan-b btn" + (puesta ? " puesta" : "")}
        aria-haspopup="dialog" aria-expanded={abierto}
        aria-label={`Planificar: ${t.titulo}. ${puesta ? `Ahora: ${texto}` : "Sin planificar"}`}
        onClick={() => setAbierto(a => !a)}>
        <Icon name={plan?.tipo === "semana" || plan?.tipo === "semana_siguiente" ? "calendar3" : "sun"} size={13} color="currentColor" />
        <span>{texto}</span>
        <Icon name="chevron-down" size={10} color="currentColor" />
      </button>
      {abierto && (
        <div ref={panelRef} role="dialog" aria-label={`Planificar: ${t.titulo}`} className="mt-plan-pop" style={estilo}>
          <div className="mt-plan-cab">
            <b>Planificar</b>
            <button type="button" className="mt-x btn" aria-label="Cerrar" onClick={cerrar}>
              <Icon name="x" size={14} color="currentColor" />
            </button>
          </div>
          <div className="mt-plan-rap">
            <button type="button" aria-pressed={plan?.tipo === "hoy"}
              onClick={() => planificar({ tipo: "hoy" })}><Icon name="sun" size={12} color="currentColor" /> Hoy <small>{conteo[hoyIso] || 0}/{tope}</small></button>
            <button type="button" aria-pressed={plan?.tipo === "dia" && plan.dia === manana(hoy)}
              onClick={() => planificar({ tipo: "manana" })}>Mañana</button>
            <button type="button" aria-pressed={plan?.tipo === "semana"}
              onClick={() => planificar({ tipo: "semana" })}>Esta semana, sin día</button>
            <button type="button" aria-pressed={plan?.tipo === "semana_siguiente"}
              onClick={() => planificar({ tipo: "semana_siguiente" })}>La semana que viene, sin día</button>
          </div>
          {lleno5 && !lleno && <p className="mt-plan-nota">Hoy ya tiene {conteo[hoyIso]} tareas.</p>}
          {lleno && (
            <div className="mt-plan-aviso" role="alert">
              <span>Hoy ya tiene {tope} tareas. ¿Añadir esta igualmente?</span>
              <button type="button" onClick={() => planificar({ ...lleno, forzar: true })}>Añadir igualmente</button>
              <button type="button" onClick={() => planificar({ tipo: "semana" })}>Mandar a esta semana, sin día</button>
            </div>
          )}
          <div className="mt-plan-et">Elige un día</div>
          {/* UNA sola tira de catorce dias: esta semana y la que viene, sin titulos que repitan los atajos de arriba */}
          <div className="mt-plan-tira dos" role="group" aria-label="Elige un día">
            {[...dias, ...diasQueVienen].map(d => (
              <button key={d.iso} type="button" disabled={d.pasado} aria-label={conteo[d.iso] ? `${d.nombre}, ${conteo[d.iso]} ${conteo[d.iso] === 1 ? "tarea" : "tareas"}` : d.nombre}
                aria-pressed={plan?.dia === d.iso && plan?.tipo === "dia"}
                onClick={() => planificar({ tipo: "dia", dia: d.iso })}>
                <span>{d.letra}</span><b>{d.numero}</b>
                <small>{conteo[d.iso] ? conteo[d.iso] : ""}</small>
              </button>
            ))}
          </div>
          <div className="mt-plan-et">Recordatorio en Microsoft To Do</div>
          {sinMs || sinPermiso ? (
            <div className="mt-plan-aviso">
              <span>{sinMs ? "Microsoft no está conectado. Tus tareas funcionan igual; el recordatorio es opcional."
                           : "Reconecta Microsoft para activar los recordatorios en To Do."}</span>
              {onConectarMicrosoft && (
                <button type="button" onClick={onConectarMicrosoft}>{sinMs ? "Conectar Microsoft" : "Reconectar Microsoft"}</button>
              )}
            </div>
          ) : !tieneDia ? (
            <p className="mt-plan-nota">Elige primero un día: el recordatorio necesita fecha. «Esta semana» y «Semana que viene» no la tienen.</p>
          ) : (
            <>
              <div className="mt-plan-hora">
                <label htmlFor={`h-${t.id ?? t.task_id}`}>Hora del aviso · {etiquetaDia(plan.dia)}</label>
                <select id={`h-${t.id ?? t.task_id}`} value={hora} onChange={e => setHora(e.target.value)}>
                  {HORAS_AVISO.map(h => <option key={h} value={h}>{h}</option>)}
                  <option value="">Sin aviso (solo fecha)</option>
                </select>
              </div>
              <button type="button" className="mt-plan-cta" onClick={ponerRecordatorio}>
                <Icon name="list-check" size={13} color="currentColor" />
                {t.recordatorio ? "Cambiar recordatorio" : "Recordatorio en To Do"}
              </button>
            </>
          )}
          {aviso && (
            <div className="mt-plan-aviso" role="alert">
              <span>{aviso.texto}</span>
              {aviso.reintentar && <button type="button" onClick={ponerRecordatorio}>Reintentar</button>}
            </div>
          )}
          {puesta && (
            <button type="button" className="mt-plan-quitar" onClick={() => planificar({ tipo: "ninguno" })}>Quitar de mi plan</button>
          )}
        </div>
      )}
    </div>
  );
}
